var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import '@toast-ui/editor/dist/toastui-editor.css';
import { Editor } from '@toast-ui/react-editor';
import React, { forwardRef, useState, useEffect, useMemo } from 'react';
import { COLORS } from 'renderer/styles';
import { removeZeroWidthSpace, uniq } from 'renderer/utils';
import styled from 'styled-components';
export var TuiEditor = Editor;
export var MyEditor = forwardRef(function (props, ref) {
    var _a = useState(''), initialValue = _a[0], setInitialValue = _a[1];
    var variableRegex = useMemo(function () {
        return generateVariableRegex(props.variables);
    }, [props.variables]);
    useEffect(function () {
        var initialValueToMD = convertToMD(props.initialValue, variableRegex);
        if (initialValueToMD !== initialValue) {
            setInitialValue(initialValueToMD);
            ref.current.getInstance().setMarkdown(initialValueToMD);
        }
    }, [props.initialValue, variableRegex]);
    return (React.createElement(EditorWrapper, null,
        React.createElement(Editor, { height: props.height, placeholder: props.placeholder, onChange: function () {
                var editorInstance = ref.current.getInstance();
                if (!editorInstance)
                    return;
                props.onChange(removeMDMarker(removeZeroWidthSpace(editorInstance.getMarkdown()), props.variables));
                if (props.variables) {
                    var editor = editorInstance.getEditorElements().mdEditor;
                    var widgetSpans_1 = editor.querySelectorAll('.tui-widget');
                    // 다음 렌더링 프레임에서 실행 (UI 변경을 부드럽게 처리)
                    requestAnimationFrame(function () {
                        widgetSpans_1 === null || widgetSpans_1 === void 0 ? void 0 : widgetSpans_1.forEach(function (item) {
                            var previousNode = item.previousSibling;
                            // 이전 노드가 텍스트 노드이고 해당 내용이 "#"만 포함하는 경우 이를 제거
                            if ((previousNode === null || previousNode === void 0 ? void 0 : previousNode.nodeType) === Node.TEXT_NODE && previousNode.textContent.trim() === '#') {
                                previousNode.textContent = previousNode.textContent.replace('#', '');
                            }
                        });
                    });
                }
            }, ref: ref, toolbarItems: [], widgetRules: !!props.variables ? [
                {
                    rule: variableRegex,
                    toDOM: function (text) {
                        var span = document.createElement('span');
                        span.innerHTML = "<span style=\"background-color:".concat(COLORS.primary5, "; font-weight: 800; color: ").concat(COLORS.primary1, "\">").concat(text, "</span>");
                        return span;
                    }
                }
            ] : [], hideModeSwitch: true, usageStatistics: false, initialEditType: "markdown", initialValue: initialValue, useCommandShortcut: false })));
});
MyEditor.displayName = 'MyEditor';
var addWidgetMarker = function (text, index) {
    if (index === void 0) { index = 0; }
    return "$$$widget".concat(index, " ").concat(text, "$$$");
};
export var removeMDMarker = function (text, variables) {
    var matched = text.match(/\$\$widget0 #{[ㄱ-ㅎ|ㅏ-ㅣ|가-힣|\w|\s]+}\$\$/g);
    var variableRegex = generateVariableRegex(variables);
    var result = text;
    if (!!variableRegex) {
        uniq(matched).map(function (match) {
            if (match && typeof match === 'string') {
                var values = match.match(variableRegex);
                if (values && values[0] && typeof values[0] === 'string') {
                    var variable = values[0];
                    var regex = new RegExp("\\$\\$widget0 ".concat(variable, "\\$\\$"), 'g');
                    result = result.replace(regex, variable);
                }
            }
        });
    }
    var returnText = result || '';
    // 한글, 영어아닌 글에 붙는 \를 제거
    returnText = returnText.replace(/\\(?![ㄱ-ㅎ|ㅏ-ㅣ|가-힣|a-z|\s|\d])/g, '');
    //-- remove BR tags and replace them with line break
    returnText = returnText.replace(/<br>/gi, '\n');
    returnText = returnText.replace(/<br\s\/>/gi, '\n');
    returnText = returnText.replace(/<br\/>/gi, '\n');
    //-- remove P and A tags but preserve what's inside of them
    returnText = returnText.replace(/<p.*>/gi, '\n');
    returnText = returnText.replace(/<a.*href="(.*?)".*>(.*?)<\/a>/gi, ' $2 ($1)');
    //-- remove all inside SCRIPT and STYLE tags
    returnText = returnText.replace(/<script.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/script>/gi, '');
    returnText = returnText.replace(/<style.*>[\w\W]{1,}(.*?)[\w\W]{1,}<\/style>/gi, '');
    //-- remove all else
    // returnText = returnText.replace(/<(?:.|\s)*?>/g, '');
    //-- get rid of more than 2 multiple line breaks:
    // returnText = returnText.replace(/(?:(?:\r\n|\r|\n)\s*){2,}/gim, '\n\n');
    //-- get rid of more than 2 spaces:
    // returnText = returnText.replace(/ +(?= )/g, '');
    //-- get rid of html-encoded characters:
    returnText = returnText.replace(/&nbsp;/gi, ' ');
    returnText = returnText.replace(/&amp;/gi, '&');
    returnText = returnText.replace(/&quot;/gi, '"');
    returnText = returnText.replace(/&lt;/gi, '<');
    returnText = returnText.replace(/&gt;/gi, '>');
    return returnText;
};
export var convertToMD = function (value, variableRegex) {
    if (!!variableRegex) {
        var matched = (value === null || value === void 0 ? void 0 : value.match(new RegExp(variableRegex, 'g'))) || [];
        uniq(matched).map(function (match) {
            if (typeof match === 'string') {
                value = value.replace(new RegExp(match, 'g'), addWidgetMarker(match));
            }
        });
    }
    return value;
};
var EditorWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  & .toastui-editor-defaultUI {\n    border: 0px;\n  }\n  & .toastui-editor-toolbar {\n    display: none;\n  }\n  & .toastui-editor-defaultUI .ProseMirror {\n    padding: 0px;\n  }\n  .toastui-editor-ww-container .toastui-editor-contents {\n    padding: 0px;\n  }\n  & .toastui-editor-md-delimiter,\n  .toastui-editor-md-thematic-break,\n  .toastui-editor-md-link,\n  .toastui-editor-md-table,\n  .toastui-editor-md-block-quote {\n    color: unset !important;\n  }\n\n  & .toastui-editor-md-strong {\n    font-weight: unset !important;\n  }\n  & .toastui-editor-md-strike {\n    text-decoration: none !important;\n  }\n  & .toastui-editor-md-code-block-line-background {\n    background-color: white !important;\n  }\n  & .toastui-editor-md-code.toastui-editor-md-marked-text {\n    color: black !important;\n  }\n  & .toastui-editor-md-code {\n    background-color: white !important;\n    padding: 0;\n    letter-spacing: -0.3px;\n  }\n  .toastui-editor-md-emph {\n    font-style: normal;\n  }\n"], ["\n  & .toastui-editor-defaultUI {\n    border: 0px;\n  }\n  & .toastui-editor-toolbar {\n    display: none;\n  }\n  & .toastui-editor-defaultUI .ProseMirror {\n    padding: 0px;\n  }\n  .toastui-editor-ww-container .toastui-editor-contents {\n    padding: 0px;\n  }\n  & .toastui-editor-md-delimiter,\n  .toastui-editor-md-thematic-break,\n  .toastui-editor-md-link,\n  .toastui-editor-md-table,\n  .toastui-editor-md-block-quote {\n    color: unset !important;\n  }\n\n  & .toastui-editor-md-strong {\n    font-weight: unset !important;\n  }\n  & .toastui-editor-md-strike {\n    text-decoration: none !important;\n  }\n  & .toastui-editor-md-code-block-line-background {\n    background-color: white !important;\n  }\n  & .toastui-editor-md-code.toastui-editor-md-marked-text {\n    color: black !important;\n  }\n  & .toastui-editor-md-code {\n    background-color: white !important;\n    padding: 0;\n    letter-spacing: -0.3px;\n  }\n  .toastui-editor-md-emph {\n    font-style: normal;\n  }\n"])));
var generateVariableRegex = function (variables) {
    return !!variables ? new RegExp(variables.join('|')) : undefined;
};
var templateObject_1;
